import { forwardRef, useId, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import type { InputProps } from './types'
import { cn } from '@/shared/cn'

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hint,
      error,
      leftIcon,
      rightIcon,
      showToggle = false,
      noShadow: _noShadow = false,
      size: _size = 'md',
      disabled = false,
      type = 'text',
      highlightOnlyOnFill = false,
      floatingLabel = false,
      id: externalId,
      'aria-describedby': externalDescribedBy,
      className,
      onFocus,
      onBlur,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId()
    const inputId = externalId ?? `input-${autoId}`
    const hintId = `${inputId}-hint`
    const errorId = `${inputId}-error`

    const [focused, setFocused] = useState(false)
    const [showPassword, setShowPassword] = useState(false)

    const effectiveType =
      type === 'password' && showToggle && showPassword ? 'text' : type

    const describedByIds = [
      externalDescribedBy,
      error ? errorId : hint ? hintId : undefined,
    ]
      .filter(Boolean)
      .join(' ')

    const wrapperBase =
      'flex items-center w-full min-h-[44px] rounded-lg px-3 border-[1.5px] transition-[border-color,box-shadow,background-color] duration-150 cursor-text'

    const wrapperState = (() => {
      if (disabled)
        return 'bg-surface-background border-border-default cursor-not-allowed opacity-60'
      if (error && focused)
        return 'border-error'
      if (error)
        return 'border-error'

      if (floatingLabel)
        return 'bg-surface-card border-primary hover:border-primary'

      if (!highlightOnlyOnFill && focused)
        return 'border-primary'

      const hasContentClass = highlightOnlyOnFill ? 'has-[input:not(:placeholder-shown)]:border-primary' : ''

      return `bg-surface-card border-border-default hover:border-border-hover ${hasContentClass}`.trim()
    })()

    const iconColor = focused && !disabled ? 'text-primary' : 'text-text-secondary'

    return (
      <div className={cn('flex flex-col gap-1.5 w-full', floatingLabel && 'relative', className)}>
        {label && !floatingLabel && (
          <label htmlFor={inputId} className="text-sm font-medium text-primary">
            {label}
          </label>
        )}

        {label && floatingLabel && (
          <label
            htmlFor={inputId}
            className={cn(
              'absolute -top-[8px] left-3 px-1 text-xs font-medium bg-surface-card pointer-events-none z-[1]',
              error ? 'text-error' : 'text-primary',
              disabled && 'opacity-60',
            )}
          >
            {label}
          </label>
        )}

        <div
          className={`${wrapperBase} ${wrapperState}`}
          onClick={() => {
            if (!disabled) {
              const inputEl = document.getElementById(inputId) as HTMLInputElement | null
              inputEl?.focus()
            }
          }}
        >
          {leftIcon && (
            <span className={`flex items-center shrink-0 mr-2 transition-colors duration-150 ${iconColor}`} aria-hidden="true">
              {leftIcon}
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            type={effectiveType}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={describedByIds || undefined}
            className={cn("flex-1 min-w-0 border-none outline-none bg-transparent font-sans text-sm font-normal leading-normal text-text-primary placeholder:text-text-secondary disabled:cursor-not-allowed")}
            onFocus={(e) => {
              setFocused(true)
              onFocus?.(e)
            }}
            onBlur={(e) => {
              setFocused(false)
              onBlur?.(e)
            }}
            {...rest}
          />

          {rightIcon && !showToggle && (
            <span className={`flex items-center shrink-0 ml-2 transition-colors duration-150 ${iconColor}`} aria-hidden="true">
              {rightIcon}
            </span>
          )}

          {showToggle && type === 'password' && (
            <button
              type="button"
              className="flex items-center shrink-0 ml-2 p-0 bg-transparent border-none cursor-pointer text-text-secondary hover:text-text-primary transition-colors duration-150 disabled:text-text-disabled disabled:cursor-not-allowed leading-none"
              disabled={disabled}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              onClick={(e) => {
                e.stopPropagation()
                setShowPassword((prev) => !prev)
              }}
            >
              {showPassword ? (
                <Eye size={18} strokeWidth={1.75} />
              ) : (
                <EyeOff size={18} strokeWidth={1.75} />
              )}
            </button>
          )}
        </div>

        {error ? (
          <span id={errorId} className="text-xs text-error" role="alert">
            {error}
          </span>
        ) : hint ? (
          <span id={hintId} className="text-xs text-text-secondary">
            {hint}
          </span>
        ) : null}
      </div>
    )
  },
)

Input.displayName = 'Input'

export { Input }
